import React, { useState, useEffect } from 'react';
import AdminLayout from '@/components/layout/AdminLayout';
import { API } from '@/App';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Stethoscope, Video, User, Clock, CheckCircle, Play } from 'lucide-react';

const assessmentTypeLabels = {
  before_medicine: 'Before Medicine',
  after_medicine: 'After Medicine',
  transition: 'Transition Period',
  gait: 'Gait Analysis',
  posture: 'Posture Assessment',
  speech: 'Speech Assessment'
};

export default function AdminClinical() {
  const [assessments, setAssessments] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('uploaded');
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [reviewNotes, setReviewNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const usersRes = await axios.get(`${API}/users?role=subscriber`);
      setUsers(usersRes.data);
      
      // Fetch assessments for all subscribers
      const allAssessments = [];
      for (const user of usersRes.data) {
        try {
          const res = await axios.get(`${API}/video-assessments/${user.id}`);
          allAssessments.push(...res.data.map(a => ({ ...a, subscriber: user })));
        } catch (e) {
          // Ignore errors for individual users
        }
      }
      setAssessments(allAssessments);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleReview = async () => {
    if (!selectedAssessment || !reviewNotes.trim()) {
      toast.error('Please enter review notes');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/video-assessments/${selectedAssessment.id}/review?notes=${encodeURIComponent(reviewNotes)}`);
      toast.success('Review submitted');
      setSelectedAssessment(null);
      setReviewNotes('');
      fetchData();
    } catch (error) {
      toast.error('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const filteredAssessments = assessments.filter(a => 
    statusFilter === 'all' || a.status === statusFilter
  );

  const pendingCount = assessments.filter(a => a.status === 'uploaded').length;
  const reviewedCount = assessments.filter(a => a.status === 'reviewed').length;

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-['Outfit'] font-bold text-adm-primary text-adm-primary" data-testid="clinical-title">
              Clinical Review
            </h1>
            <p className="text-adm-tertiary text-adm-primary/60">Review video assessments</p>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 text-sm">
              <Badge className="bg-amber-500/20 text-amber-400">
                {pendingCount} pending
              </Badge>
              <Badge className="bg-[#4E7A6B]/20 text-[#4E7A6B]">
                {reviewedCount} reviewed
              </Badge>
            </div>
            
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-[150px] bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]">
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="uploaded">Pending Review</SelectItem>
                <SelectItem value="reviewed">Reviewed</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Assessments */}
        <div className="space-y-4">
          {filteredAssessments.map(assessment => (
            <Card key={assessment.id} className="admin-card bg-adm-surface border-white/[0.06]">
              <CardContent className="p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-purple-500/20 flex items-center justify-center">
                      <Video className="w-6 h-6 text-purple-400" />
                    </div>
                    <div>
                      <h3 className="font-['Outfit'] font-semibold text-adm-primary text-adm-primary">
                        {assessmentTypeLabels[assessment.assessment_type] || assessment.assessment_type}
                      </h3>
                      <p className="text-sm text-adm-tertiary text-adm-primary/50">
                        {assessment.subscriber?.name} • {assessment.subscriber?.email}
                      </p>
                    </div>
                  </div>
                  
                  <Badge className={assessment.status === 'reviewed' 
                    ? 'bg-[#4E7A6B]/20 text-[#4E7A6B]' 
                    : 'bg-amber-500/20 text-amber-400'
                  }>
                    {assessment.status === 'reviewed' ? (
                      <><CheckCircle className="w-3 h-3 mr-1" /> Reviewed</>
                    ) : (
                      <><Clock className="w-3 h-3 mr-1" /> Pending</>
                    )}
                  </Badge>
                </div>
                
                <div className="flex items-center gap-4 text-sm text-adm-tertiary text-adm-primary/50 mb-4">
                  <span>Uploaded: {new Date(assessment.uploaded_at || assessment.created_at).toLocaleDateString()}</span>
                  {assessment.reviewed_at && (
                    <span>Reviewed: {new Date(assessment.reviewed_at).toLocaleDateString()}</span>
                  )}
                </div>
                
                {assessment.review_notes && (
                  <div className="p-4 rounded-lg bg-[#4E7A6B]/10 border border-[#4E7A6B]/30 mb-4">
                    <p className="text-xs text-[#4E7A6B] uppercase tracking-wider mb-1">Review Notes</p>
                    <p className="text-slate-700 text-adm-primary/80">{assessment.review_notes}</p>
                  </div>
                )}
                
                {assessment.status === 'uploaded' && (
                  <Dialog>
                    <DialogTrigger asChild>
                      <Button 
                        onClick={() => setSelectedAssessment(assessment)}
                        className="copper-gradient text-[#0B0F19]"
                      >
                        <Stethoscope className="w-4 h-4 mr-2" />
                        Submit Review
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="bg-adm-surface bg-adm-surface border-white/[0.06] border-white/[0.06]">
                      <DialogHeader>
                        <DialogTitle className="text-adm-primary text-adm-primary">
                          Review: {assessmentTypeLabels[assessment.assessment_type]}
                        </DialogTitle>
                      </DialogHeader>
                      <div className="space-y-4 mt-4">
                        <div className="p-4 rounded-lg bg-adm-bg bg-adm-bg">
                          <p className="text-sm text-adm-secondary text-adm-primary/60">
                            Subscriber: {assessment.subscriber?.name}
                          </p>
                          <p className="text-sm text-adm-secondary text-adm-primary/60">
                            Assessment Type: {assessmentTypeLabels[assessment.assessment_type]}
                          </p>
                        </div>
                        
                        <div>
                          <label className="text-sm font-medium text-slate-700 text-adm-primary/80 mb-1.5 block">
                            Clinical Notes
                          </label>
                          <Textarea
                            value={reviewNotes}
                            onChange={(e) => setReviewNotes(e.target.value)}
                            placeholder="Enter your clinical observations and findings..."
                            className="bg-adm-bg bg-adm-bg border-white/[0.06] border-white/[0.06] min-h-[150px]"
                          />
                        </div>
                        
                        <Button 
                          onClick={handleReview}
                          className="w-full copper-gradient text-[#0B0F19]"
                          disabled={submitting}
                        >
                          {submitting ? 'Submitting...' : 'Submit Review'}
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                )}
              </CardContent>
            </Card>
          ))}
          
          {filteredAssessments.length === 0 && (
            <Card className="admin-card bg-adm-surface border-white/[0.06]">
              <CardContent className="p-8 text-center text-adm-tertiary text-adm-primary/50">
                <Video className="w-12 h-12 mx-auto mb-4 opacity-30" />
                No video assessments found
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </AdminLayout>
  );
}
